import Spinner from "./Spinner";
import Select from "react-select";
import ApiServices from "../api/services";
import { mediaUrl, baseUrl, googleApiKey } from "../utils/config";
import camera from "../assets/img/camera.png";
import fileImage from "../assets/img/fileImage.png";
import countryList from "react-select-country-list";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { useLoggedContext } from "../context/provider";
import { Fragment, useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";

// Custom places autocomplete hook embedded directly in the file
const usePlacesAutocomplete = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Clear suggestions
  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setShowSuggestions(false);
  }, []);

  // Function to create a proxy URL for Google API calls
  const createProxyUrl = (googleApiUrl) => {
    // Option 1: Use a free CORS proxy service (for development only)
    return `https://corsproxy.io/?${encodeURIComponent(googleApiUrl)}`;
  };

  // Fetch suggestions with debounce
  const debouncedFetchSuggestions = useCallback(
    (() => {
      let timeoutId = null;
      return (input) => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        
        if (!input || input.length < 3) {
          clearSuggestions();
          return;
        }
        
        timeoutId = setTimeout(async () => {
          setLoading(true);
          setError(null);
          
          try {
            // Use proxy for Google Places API
            const googleApiUrl = `https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${encodeURIComponent(input)}&types=address&key=${googleApiKey}`;
            const proxyUrl = createProxyUrl(googleApiUrl);
            
            const response = await axios.get(proxyUrl);
            
            if (response.data && response.data.predictions) {
              setSuggestions(response.data.predictions);
              setShowSuggestions(true);
            } else {
              setSuggestions([]);
              setError("No suggestions found");
            }
          } catch (error) {
            console.error("Error fetching address suggestions:", error);
            setError("Failed to fetch address suggestions");
            setSuggestions([]);
          } finally {
            setLoading(false);
          }
        }, 300);
      };
    })(),
    [clearSuggestions]
  );

  // Get place details
  const getPlaceDetails = useCallback(async (placeId) => {
    if (!placeId) return null;

    try {
      // Use proxy for Google Places Details API
      const googleApiUrl = `https://maps.googleapis.com/maps/api/place/details/json?place_id=${placeId}&fields=address_components,formatted_address,geometry&key=${googleApiKey}`;
      const proxyUrl = createProxyUrl(googleApiUrl);
      
      const response = await axios.get(proxyUrl);

      if (response.data && response.data.result) {
        return response.data.result;
      }
      return null;
    } catch (error) {
      console.error("Error fetching place details:", error);
      setError("Failed to fetch place details");
      return null;
    }
  }, []);

  return {
    suggestions,
    showSuggestions,
    loading,
    error,
    debouncedFetchSuggestions,
    getPlaceDetails,
    clearSuggestions,
  };
};

const AddNewSettingModal = ({ isOpen, setIsOpen, data, editMode, onSuccess }) => {
  // Context
  const { btnLoading, setBtnLoading, openSuccessModal } = useLoggedContext();

  // Use State
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [country, setCountry] = useState(null);
  const [countryErr, setCountryErr] = useState("");
  const [settingName, setSettingName] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [contactPerson, setContactPerson] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [message, setMessage] = useState({ text: "", color: "" });

  // New state variables for occupation, city, state, postal code
  const [occupation, setOccupation] = useState(null);
  const [occupations, setOccupations] = useState([]);
  const [occupationsLoading, setOccupationsLoading] = useState(true);
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [addressNewError, setAddressNewError] = useState("");

  // Error Handling Use state
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [settingNameError, setSettingNameError] = useState("");
  const [selectedFileError, setSelectedFileError] = useState("");
  const [contactPersonError, setContactPersonError] = useState("");

  // New error states
  const [occupationError, setOccupationError] = useState("");
  const [cityError, setCityError] = useState("");
  const [stateError, setStateError] = useState("");
  const [postalCodeError, setPostalCodeError] = useState("");

  // Country list
  const countriesList = useMemo(() => countryList().getData(), []);

  // Places autocomplete for address
  const {
    suggestions: addressSuggestions,
    showSuggestions: showAddressSuggestions,
    loading: addressLoading,
    error: addressError,
    debouncedFetchSuggestions,
    getPlaceDetails,
    clearSuggestions,
  } = usePlacesAutocomplete();

  // Fetch occupations
  const fetchOccupations = async () => {
    setOccupationsLoading(true);
    setOccupations([]);

    try {
      const response = await axios.get(`${baseUrl}employee/occupations`);
      console.log("Occupations response:", response);

      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        const occupationsArray = response.data.data;

        if (occupationsArray.length === 0) {
          console.warn("API returned empty occupations list");
        } else {
          const formattedData = occupationsArray.map((occupation) => ({
            value: occupation.id,
            label: occupation.name,
          }));

          setOccupations(formattedData);
          setOccupationError("");
        }
      } else {
        console.error("Unexpected API response structure:", response);
        setOccupationError("Unexpected data format from server");
      }
    } catch (error) {
      console.error("Failed to fetch occupations:", error);
      const errorMessage = error.response?.data?.message || "Failed to load occupations. Please try again.";

      setOccupationError(errorMessage);
    } finally {
      setOccupationsLoading(false);
    }
  };

  // Handle address selection from autocomplete
  const handleAddressSelect = async (placeId, description) => {
    setAddress(description);
    setAddressNewError("");
    clearSuggestions();

    try {
      const placeDetails = await getPlaceDetails(placeId);
      
      if (placeDetails) {
        const addressComponents = placeDetails.address_components || [];
        
        // Initialize variables for address parts
        let streetNumber = "";
        let route = "";
        let cityValue = "";
        let stateValue = "";
        let postalCodeValue = "";
        let countryCode = "";
        let countryName = "";

        // Extract address components
        addressComponents.forEach((component) => {
          const types = component.types;

          if (types.includes("street_number")) {
            streetNumber = component.long_name;
          } else if (types.includes("route")) {
            route = component.long_name;
          } else if (types.includes("locality") || types.includes("postal_town")) {
            cityValue = component.long_name;
          } else if (types.includes("administrative_area_level_1")) {
            stateValue = component.long_name;
          } else if (types.includes("postal_code")) {
            postalCodeValue = component.long_name;
          } else if (types.includes("country")) {
            countryName = component.long_name;
            countryCode = component.short_name;
          }
        });

        // Create a street address if we have the components
        let formattedAddress = description;
        if (streetNumber && route) {
          formattedAddress = `${streetNumber} ${route}`;
        } else if (route) {
          formattedAddress = route;
        }

        // Find matching country in our list
        const countryOption = countriesList.find(
          (c) => c.label === countryName || c.value === countryCode
        );

        // Update all the form fields
        setAddress(formattedAddress);
        setCity(cityValue || "");
        setState(stateValue || "");
        setPostalCode(postalCodeValue || "");
        
        if (countryOption) {
          setCountry(countryOption);
          setCountryErr("");
        }

        // Clear any errors
        setAddressNewError("");
        setCityError("");
        setStateError("");
        setPostalCodeError("");
      }
    } catch (error) {
      console.error("Error fetching place details:", error);
      setMessage({
        text: "Failed to get address details. Please try again or enter manually.",
        color: "#ed4337",
      });
    }
  };

  // Validations
  const isValidForm = () => {
    let isValidData = true;

    if (!settingName) {
      setSettingNameError("Required");
      isValidData = false;
    }

    if (!contactPerson) {
      setContactPersonError("Required");
      isValidData = false;
    }

    if (!email) {
      setEmailError("Required");
      isValidData = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setEmailError("Invalid Email Address");
        isValidData = false;
      }
    }

    if (!phone) {
      setPhoneError("Required");
      isValidData = false;
    } else if (!/^[0-9+()-\s]+$/.test(phone)) {
      setPhoneError("Phone Number Only");
      isValidData = false;
    }

    if (!country) {
      setCountryErr("Required");
      isValidData = false;
    }

    if (!address) {
      setAddressNewError("Required");
      isValidData = false;
    }

    if (!city) {
      setCityError("Required");
      isValidData = false;
    }

    if (!state) {
      setStateError("Required");
      isValidData = false;
    }

    if (!postalCode) {
      setPostalCodeError("Required");
      isValidData = false;
    }

    if (!occupation) {
      setOccupationError("Required");
      isValidData = false;
    }

    if (!selectedImage) {
      setSelectedFileError("Please Upload a Image");
      isValidData = false;
    }

    return isValidData;
  };

  // Image Upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setSelectedFileError("");
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Image delete
  const handleImageDelete = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    const isValid = isValidForm();
    if (!isValid) {
      console.log("Form validation failed. Please check for errors.");
      return;
    }

    const formData = new FormData();
    if (selectedImage instanceof File) {
      formData.append("logo", selectedImage);
    }
    formData.append("name", settingName);
    formData.append("contact_person_name", contactPerson);
    if (!data) {
      formData.append("email", email);
    }
    formData.append("phone_number", phone);
    formData.append("country", country?.label);
    formData.append("address", address);

    // Add new form data
    formData.append("city", city);
    formData.append("state", state);
    formData.append("postal_code", postalCode);
    formData.append("occupation_id", occupation?.value);

    try {
      setBtnLoading(true);
      const response = data
        ? await ApiServices.settings.UpdateAddNewSetting(data?.nursery[0]?.id, formData)
        : await ApiServices.settings.AddNewSetting(formData);

      if (response.data.code === 200) {
        setBtnLoading(false);
        closeModal();
        onSuccess();
        openSuccessModal({
          title: "Success!",
          message: data ? "Nursery updated successfully." : "Nursery added successfully.",
          onPress: (close) => {
            close();
            onSuccess();
          },
        });
      }
    } catch (error) {
      console.error("Error updating/adding setting:", error);
      setBtnLoading(false);
      setMessage({
        text: error?.response?.data?.message || "An error occurred.",
        color: "#ed4337",
      });
    }
  };

  // useEffects
  useEffect(() => {
    if (isOpen) {
      fetchOccupations();
    }
  }, [isOpen]);

  useEffect(() => {
    if (data && editMode && isOpen) {
      setSettingName(data?.nursery[0]?.name);
      setEmail(data?.email);
      setAddress(data?.nursery[0]?.address);
      setPhone(data?.nursery[0]?.phone_number);
      setSelectedImage(data?.nursery[0]?.logo);
      setContactPerson(data?.nursery[0]?.contact_person_name);
      setCountry({ label: data?.nursery[0]?.country, value: data?.nursery[0]?.country });

      // Set new form data from existing data if available
      setCity(data?.nursery[0]?.city || "");
      setState(data?.nursery[0]?.state || "");
      setPostalCode(data?.nursery[0]?.postal_code || "");

      // Set occupation if available
      if (data?.nursery[0]?.occupation) {
        const occupationData = {
          value: data?.nursery[0]?.occupation?.id,
          label: data?.nursery[0]?.occupation?.name || "Unknown Occupation",
        };
        setOccupation(occupationData);
      } else {
        setOccupation(null);
      }

      data?.nursery[0]?.logo ? setImagePreview(mediaUrl + data?.nursery[0]?.logo) : setImagePreview(null);
    } else {
      setSettingName("");
      setEmail("");
      setContactPerson("");
      setCountry(null);
      setAddress("");
      setPhone("");

      // Reset new form data
      setCity("");
      setState("");
      setPostalCode("");
      setOccupation(null);
    }
  }, [data, editMode, isOpen]);

  // Close Modal
  const closeModal = () => {
    setPhone("");
    setEmail("");
    setAddress("");
    setCountry(null);
    setIsOpen(false);
    setPhoneError("");
    setEmailError("");
    setCountryErr("");
    setSettingName("");
    setAddressNewError("");
    setContactPerson("");
    setBtnLoading(false);
    setImagePreview(null);
    setSelectedImage(null);
    setSettingNameError("");
    setSelectedFileError("");
    setContactPersonError("");

    // Reset new field values and errors
    setCity("");
    setState("");
    setPostalCode("");
    setOccupation(null);
    setCityError("");
    setStateError("");
    setPostalCodeError("");
    setOccupationError("");

    setMessage({ text: "", color: "" });
    clearSuggestions();
  };

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-75"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-75"
              >
                <Dialog.Panel className="w-full max-w-4xl overflow-hidden rounded-2xl bg-white p-8 text-center align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-between">
                    <Dialog.Title as="h3" className="font-poppins text-lg font-semibold leading-7 text-secondary-color">
                      {data ? "Edit Setting" : "Add New Setting"}
                    </Dialog.Title>
                    <XMarkIcon onClick={closeModal} className="text-info-color h-8 w-8 cursor-pointer" />
                  </div>

                  <form encType="multipart/form-data" className="sm:mx-4" onSubmit={handleFormSubmit}>
                    <div className="text-left">
                      <div className="flex flex-col">
                        <div className="relative">
                          <div className="my-5 grid grid-cols-2 gap-6">
                            <div className="col-span-2 text-center">
                              <div className="group relative mx-auto h-40 w-40 rounded-full">
                                {imagePreview ? (
                                  <>
                                    <img src={imagePreview} alt="selected img" className="h-full w-full rounded-full object-cover" />
                                    <div
                                      className="font-poppins absolute top-0 right-0 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-red-500 p-2 text-sm text-white opacity-0 transition-all duration-200 group-hover:opacity-100"
                                      onClick={handleImageDelete}
                                    >
                                      X
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <label
                                      htmlFor="image-upload"
                                      className="absolute top-0 right-0 flex h-full w-full cursor-pointer items-center justify-center"
                                    >
                                      <img src={fileImage} alt="add img" className="" />
                                    </label>
                                    <div className="absolute -bottom-4 -right-5">
                                      <input type="file" accept="image/*" id="image-upload" className="hidden" onChange={handleImageUpload} />
                                      <label htmlFor="image-upload">
                                        <img src={camera} alt="Camera Icon" className="cursor-pointer" />
                                      </label>
                                    </div>
                                  </>
                                )}
                              </div>
                              {selectedFileError && <p className="mt-3 text-sm text-red-500">{selectedFileError}</p>}
                            </div>

                            <div className="col-span-1">
                              <div className="mb-2 ml-4 text-[11px] text-grayColor300 xl:text-sm">
                                Setting Name <span className="text-red-600">*</span>
                                {settingNameError && <span className="ml-1 text-sm text-red-600">{settingNameError}</span>}
                              </div>
                              <input
                                className="w-full rounded-full border-[1px] border-[#C5C5C5] px-6 py-3 text-sm font-normal text-[#474747]"
                                placeholder="Enter setting name"
                                type="text"
                                name="settingName"
                                maxLength={25}
                                value={settingName}
                                onChange={(e) => {
                                  setSettingName(e.target.value);
                                  setSettingNameError("");
                                }}
                              />
                            </div>

                            <div className="col-span-1">
                              <div className="mb-2 ml-4 text-[11px] text-grayColor300 xl:text-sm">
                                Contact Person <span className="text-red-600">*</span>
                                {contactPersonError && <span className="ml-1 text-sm text-red-600">{contactPersonError}</span>}
                              </div>
                              <input
                                className="w-full rounded-full border-[1px] border-[#C5C5C5] px-6 py-3 text-sm font-normal text-[#474747]"
                                placeholder="Enter contact person name"
                                type="text"
                                name="contactPerson"
                                value={contactPerson}
                                onChange={(e) => {
                                  setContactPerson(e.target.value);
                                  setContactPersonError("");
                                }}
                              />
                            </div>

                            <div className="col-span-1">
                              <div className="mb-2 ml-4 text-[11px] text-grayColor300 xl:text-sm">
                                Phone Number <span className="text-red-600">*</span>
                                {phoneError && <span className="ml-1 text-sm text-red-600">{phoneError}</span>}
                              </div>
                              <input
                                className="w-full rounded-full border-[1px] border-[#C5C5C5] px-6 py-3 text-sm font-normal text-[#474747]"
                                placeholder="Enter phone number"
                                type="tel"
                                name="phone"
                                value={phone}
                                onChange={(e) => {
                                  setPhone(e.target.value);
                                  setPhoneError("");
                                }}
                              />
                            </div>

                            <div className="col-span-1">
                              <div className="mb-2 ml-4 text-[11px] text-grayColor300 xl:text-sm">
                                Email <span className="text-red-600">*</span>
                                {emailError && <span className="ml-1 text-sm text-red-600">{emailError}</span>}
                              </div>
                              <input
                                className={`${editMode && "bg-gray-200"} w-full rounded-full border-[1px] border-[#C5C5C5] px-6 py-3 text-sm font-normal text-[#474747]`}
                                placeholder="Enter email address"
                                type="text"
                                name="email"
                                value={email}
                                onChange={(e) => {
                                  setEmail(e.target.value);
                                  setEmailError("");
                                }}
                                readOnly={editMode ? true : false}
                              />
                            </div>

                            <div className="col-span-1">
                              <div className="mb-2 ml-4 text-[11px] text-grayColor300 xl:text-sm">
                                Occupation <span className="text-red-600">*</span>
                                {occupationError && <span className="ml-1 text-sm text-red-600">{occupationError}</span>}
                              </div>
                              <Select
                                options={occupations}
                                value={occupation}
                                onChange={(value) => {
                                  setOccupation(value);
                                  setOccupationError("");
                                }}
                                styles={customStyles}
                                placeholder={
                                  occupationsLoading
                                    ? "Loading occupations..."
                                    : occupations.length === 0
                                      ? "No occupations found"
                                      : "Type to search occupation"
                                }
                                isLoading={occupationsLoading}
                                noOptionsMessage={() => "No occupations found"}
                              />
                            </div>

                            <div className="relative col-span-1">
                              <div className="mb-2 ml-4 text-[11px] text-grayColor300 xl:text-sm">
                                Address <span className="text-red-600">*</span>
                                {addressError && <span className="ml-1 text-sm text-red-600">{addressError}</span>}
                              </div>
                              <input
                                className="w-full rounded-full border-[1px] border-[#C5C5C5] px-6 py-3 text-sm font-normal text-[#474747]"
                                placeholder="Enter address"
                                type="text"
                                name="address"
                                value={address}
                                onChange={(e) => {
                                  setAddress(e.target.value);
                                  setAddressNewError("");
                                  debouncedFetchSuggestions(e.target.value);
                                }}
                              />

                              {/* Address suggestions dropdown */}
                              {showAddressSuggestions && addressSuggestions.length > 0 && (
                                <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
                                  <ul>
                                    {addressSuggestions.map((suggestion) => (
                                      <li
                                        key={suggestion.place_id}
                                        className="cursor-pointer px-4 py-2 text-left text-sm hover:bg-gray-100"
                                        onClick={() => handleAddressSelect(suggestion.place_id, suggestion.description)}
                                      >
                                        {suggestion.description}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>

                            <div className="col-span-1">
                              <div className="mb-2 ml-4 text-[11px] text-grayColor300 xl:text-sm">
                                City <span className="text-red-600">*</span>
                                {cityError && <span className="ml-1 text-sm text-red-600">{cityError}</span>}
                              </div>
                              <input
                                className="w-full rounded-full border-[1px] border-[#C5C5C5] px-6 py-3 text-sm font-normal text-[#474747]"
                                placeholder="Enter city"
                                type="text"
                                name="city"
                                value={city}
                                onChange={(e) => {
                                  setCity(e.target.value);
                                  setCityError("");
                                }}
                              />
                            </div>

                            <div className="col-span-1">
                              <div className="mb-2 ml-4 text-[11px] text-grayColor300 xl:text-sm">
                                State / County / Province <span className="text-red-600">*</span>
                                {stateError && <span className="ml-1 text-sm text-red-600">{stateError}</span>}
                              </div>
                              <input
                                className="w-full rounded-full border-[1px] border-[#C5C5C5] px-6 py-3 text-sm font-normal text-[#474747]"
                                placeholder="Enter state/province"
                                type="text"
                                name="state"
                                value={state}
                                onChange={(e) => {
                                  setState(e.target.value);
                                  setStateError("");
                                }}
                              />
                            </div>

                            <div className="col-span-1">
                              <div className="mb-2 ml-4 text-[11px] text-grayColor300 xl:text-sm">
                                Postal Code <span className="text-red-600">*</span>
                                {postalCodeError && <span className="ml-1 text-sm text-red-600">{postalCodeError}</span>}
                              </div>
                              <input
                                className="w-full rounded-full border-[1px] border-[#C5C5C5] px-6 py-3 text-sm font-normal text-[#474747]"
                                placeholder="Enter postal code"
                                type="text"
                                name="postalCode"
                                value={postalCode}
                                onChange={(e) => {
                                  setPostalCode(e.target.value);
                                  setPostalCodeError("");
                                }}
                              />
                            </div>

                            <div className="col-span-1">
                              <div className="mb-2 ml-4 text-[11px] text-grayColor300 xl:text-sm">
                                Country <span className="text-red-600">*</span>
                                {countryErr && <span className="ml-1 text-sm text-red-600">{countryErr}</span>}
                              </div>
                              <Select
                                options={countriesList}
                                value={country}
                                onChange={(value) => {
                                  setCountry(value);
                                  setCountryErr("");
                                }}
                                styles={customStyles}
                                placeholder="Type to search your country"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* message */}
                      <div className="mt-6 text-center">
                        <p className="text-sm font-medium" style={{ color: message.color || "#ed4337" }}>
                          {message.text}
                        </p>
                      </div>

                      {/* buttons */}
                      <div className="mt-8 grid grid-cols-2 gap-x-8">
                        <button type="submit" className="rounded-full bg-secondary px-7 py-2.5 text-white">
                          {btnLoading ? <Spinner /> : data ? "Update" : "Add Setting"}
                        </button>
                        <button
                          type="button"
                          className="rounded-full border-2 border-secondary bg-white px-7 py-2.5 text-secondary transition-colors duration-200 hover:bg-secondary hover:text-white"
                          onClick={closeModal}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default AddNewSettingModal;

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    height: "42px",
    minHeight: "42px",
    borderRadius: "4.375rem",
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    height: "42px",
    padding: "0 10px",
  }),
  input: (provided, state) => ({
    ...provided,
    margin: "0px",
    fontSize: "14px",
  }),
  indicatorsContainer: (provided, state) => ({
    ...provided,
    height: "42px",
  }),

  option: (provided, state) => ({
    ...provided,
    padding: "10px 12px",
    fontSize: "14px",
  }),
  placeholder: (provided, state) => ({
    ...provided,
    fontSize: "12px",
  }),
  singleValue: (provided, state) => ({
    ...provided,
    fontSize: "14px",
    color: "#474747",
    fontWeight: "500",
  }),
};